import type {BriefingBoardAcknowledgment, BriefingBoardRecipient} from "../types";

export type InboxAcknowledgmentStatus = Pick<
  BriefingBoardAcknowledgment,
  "is_actionable" | "is_read" | "read_at" | "is_expired" | "waived_reason"
>;

/** Must read now — use for badges, gate queue, and mark-read CTA (not `read_at === null`). */
export function isBriefingActionable(row: InboxAcknowledgmentStatus): boolean {
  if (row.is_actionable === true) {
    return true;
  }
  if (row.is_actionable === false) {
    return false;
  }
  if (row.is_read === false) {
    return true;
  }
  if (row.is_read === true) {
    return false;
  }
  return row.read_at == null;
}

export type InboxStatusLabel = "Unread" | "Read" | "Expired" | "Shift ended";

export function getInboxStatusLabel(row: InboxAcknowledgmentStatus): InboxStatusLabel {
  if (isBriefingActionable(row)) {
    return "Unread";
  }
  if (row.read_at) {
    return "Read";
  }
  if (row.is_expired) {
    return "Expired";
  }
  if (row.waived_reason === "shift_ended") {
    return "Shift ended";
  }
  return "Read";
}

export type InboxStatusBadgeVariant = "secondary" | "success" | "warning" | "outline";

export function getInboxStatusBadgeVariant(row: InboxAcknowledgmentStatus): InboxStatusBadgeVariant {
  const label = getInboxStatusLabel(row);
  if (label === "Unread") {
    return "secondary";
  }
  if (label === "Read") {
    return "success";
  }
  if (label === "Expired") {
    return "warning";
  }
  return "outline";
}

export function pickInboxStatus(row: BriefingBoardAcknowledgment): InboxAcknowledgmentStatus {
  return {
    is_actionable: row.is_actionable,
    is_read: row.is_read,
    read_at: row.read_at,
    is_expired: row.is_expired,
    waived_reason: row.waived_reason,
  };
}

export function actionableBriefingsForShift(
  rows: BriefingBoardAcknowledgment[],
  shiftId: number,
): BriefingBoardAcknowledgment[] {
  return rows.filter((row) => row.shift_id === shiftId && isBriefingActionable(row));
}

export type RecipientStatusLabel = "Unread" | "Read" | "Shift ended" | "Waived";

export function getRecipientStatusLabel(row: BriefingBoardRecipient): RecipientStatusLabel {
  if (row.read_at) {
    return "Read";
  }
  if (row.waived_reason === "shift_ended") {
    return "Shift ended";
  }
  if (row.waived_at) {
    return "Waived";
  }
  return "Unread";
}

export function getRecipientStatusBadgeVariant(
  row: BriefingBoardRecipient,
): InboxStatusBadgeVariant {
  const label = getRecipientStatusLabel(row);
  if (label === "Unread") {
    return "secondary";
  }
  if (label === "Read") {
    return "success";
  }
  return "outline";
}
